<template>
  <div>
    <p class="has-text-weight-bold is-size-4 mb-4">
      {{ $t("mp:application.contract_check.title") }}
    </p>
    <p class="mb-5">
      {{ $t("mp:application.contract_check.subtitle") }}
    </p>

    <template v-if="!loaded">
      <b-skeleton width="70%" :animated="true"></b-skeleton>
      <b-skeleton width="75%" :animated="true"></b-skeleton>
      <b-skeleton width="80%" :animated="true"></b-skeleton>
    </template>
    <template v-else>
      <template
        v-if="SIGNATURE_PROCESS_STATUS.NOT_READY === signatureProcess.status"
      >
        <div class="signature-process-info-card">
          <b-icon
            icon-pack="fa"
            icon="info-circle"
            type="is-blue"
            size="is-large"
            class="is-pulled-left pb-2 mr-2"
          ></b-icon>

          <p class="has-text-weight-bold mb-0">
            {{ $t("mp:application.contract_check.waiting.title") }}
          </p>
          <p>{{ $t("mp:application.contract_check.waiting.subtitle") }}</p>

          <p class="mt-5">
            {{ $t("mp:application.contract_check.waiting.message") }}
          </p>
        </div>
      </template>

      <template
        v-if="SIGNATURE_PROCESS_STATUS.EXPIRED === signatureProcess.status"
      >
        <div class="signature-process-info-card">
          <b-icon
            icon-pack="fa"
            icon="exclamation-triangle"
            type="is-danger"
            size="is-large"
            class="is-pulled-left pb-2 mr-2"
          ></b-icon>

          <p class="has-text-weight-bold mb-0">
            {{ $t("mp:application.contract_check.expired.title") }}
          </p>
          <p>
            {{ $t("mp:application.contract_check.expired.subtitle")
            }}{{ formatDateTime(signatureProcess.dueDate) }}
          </p>

          <p class="mt-5">
            {{ $t("mp:application.contract_check.expired.message") }}
          </p>
        </div>
      </template>

      <template
        v-if="SIGNATURE_PROCESS_STATUS.WITHDRAWN === signatureProcess.status"
      >
        <div class="signature-process-info-card">
          <b-icon
            icon-pack="fa"
            icon="exclamation-triangle"
            type="is-danger"
            size="is-large"
            class="is-pulled-left pb-2 mr-2"
          ></b-icon>

          <p class="has-text-weight-bold mb-0">
            {{ $t("mp:application.contract_check.withdrawn.title") }}
          </p>
          <p>{{ $t("mp:application.contract_check.withdrawn.subtitle") }}</p>

          <p class="mt-5">
            {{ $t("mp:application.contract_check.withdrawn.message") }}
          </p>
        </div>
      </template>

      <template
        v-if="SIGNATURE_PROCESS_STATUS.ERROR_FOUND === signatureProcess.status"
      >
        <div class="signature-process-info-card">
          <b-icon
            icon-pack="fa"
            icon="exclamation-triangle"
            type="is-danger"
            size="is-large"
            class="is-pulled-left pb-2 mr-2"
          ></b-icon>

          <p class="has-text-weight-bold mb-0">
            {{ $t("mp:application.contract_check.error_found.title") }}
          </p>
          <p>{{ $t("mp:application.contract_check.error_found.subtitle") }}</p>

          <p class="mt-5">
            {{ $t("mp:application.contract_check.error_found.message") }}
          </p>

          <p class="mb-0">
            <label class="has-text-weight-bold">
              {{
                $t(
                  "mp:application.contract_check.error_found.submit_date.label"
                )
              }}
            </label>
            <label>
              {{ formatDateTime(signatureProcess.submitDate) }}
            </label>
          </p>

          <p>
            <label class="has-text-weight-bold">
              {{
                $t("mp:application.contract_check.error_found.comment.label")
              }}
            </label>
            <label>
              {{ signatureProcess.comment }}
            </label>
          </p>
        </div>
      </template>

      <template
        v-if="SIGNATURE_PROCESS_STATUS.READY === signatureProcess.status"
      >
        <div class="mb-5" v-if="signatureProcess.dueDate">
          <label class="has-text-weight-bold">
            {{ $t("mp:application.contract_check.ready.due_date") }}
          </label>
          <label>
            {{ formatDateTime(signatureProcess.dueDate) }}
          </label>
        </div>

        <div
          class="mp-tile is-flex is-align-items-center signature-process-info-card mb-3"
          v-for="contract in documents"
          v-bind:key="contract.documentName"
        >
          <b-icon
            icon-pack="fa"
            :icon="
              contract.documentId
                ? contract.documentId === '-1'
                  ? 'exclamation-triangle'
                  : 'file-download'
                : 'hourglass-half'
            "
            size="is-medium"
            :type="
              contract.documentId
                ? contract.documentId === '-1'
                  ? 'is-danger'
                  : 'is-blue'
                : 'is-warning'
            "
            class="mr-2"
            @click="downloadFile(contract.documentId, contract.documentName)"
          ></b-icon>

          <a
            class="is-text-underlined"
            @click="downloadFile(contract.documentId, contract.documentName)"
            >{{ contract.documentName }}</a
          >

          <b-switch
            class="ml-auto"
            type="is-blue"
            :disabled="!contract.documentId || contract.documentId === '-1'"
            v-model="okays[contract.documentName]"
            @input="allOk"
          />
        </div>

        <br />

        <div class="my-4">
          <span>
            {{ $t("mp:application.contract_check.modal.mistake.label") }}
            <a class="is-text-underlined" @click="openMistakeModal">{{
              $t("mp:application.contract_check.modal.mistake.button.open")
            }}</a>
          </span>
        </div>

        <br />

        <br />
        <b-field v-if="allOkay">
          <b-button
            type="is-success"
            size="is-large"
            :label="$t('mp:application.contract_check.button.start')"
            :rounded="true"
            :inverted="true"
            icon-left="signature"
            icon-right="arrow-alt-circle-right"
            @click="gotoSign"
          ></b-button>
        </b-field>
      </template>
    </template>

    <generated-documents-mistake-modal ref="generatedDocumentsMistakeModal" />
  </div>
</template>

<script>
import { FLOW_POSITION, SIGNATURE_PROCESS_STATUS } from "../../utils/const";
import { mapActions, mapGetters } from "vuex";
import FileSaver from "file-saver";
import { formatDateTime, nextStepConfigOverride } from "../../utils/util";

export default {
  name: "ApplicationFlowContractCheck",
  components: {
    GeneratedDocumentsMistakeModal: () =>
      import("@/components/application/GeneratedDocumentsMistakeModal.vue"),
  },
  props: {
    position: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      FLOW_POSITION,
      SIGNATURE_PROCESS_STATUS,
      formatDateTime,
      loaded: false,
      poll: null,
      okays: {},
      allOkay: false,
    };
  },
  computed: {
    ...mapGetters({
      signatureProcess: "application/getSignatureProcess",
    }),
    documents() {
      if (this.signatureProcess && this.signatureProcess.documents) {
        return this.signatureProcess.documents;
      }
      return [];
    },
  },
  async mounted() {
    await this.pollSignatureProcess();

    for (let d in this.documents) {
      this.okays[this.documents[d].documentName] = false;
    }

    this.poll = window.setInterval(this.pollSignatureProcess, 15000);

    this.loaded = true;
  },
  beforeDestroy() {
    window.clearInterval(this.poll);
  },
  methods: {
    ...mapActions({
      savePosition: "application/savePosition",
    }),
    openMistakeModal() {
      this.$refs.generatedDocumentsMistakeModal.openModal();
    },
    allOk() {
      let ok = true;
      for (let o in this.okays) {
        ok = ok && this.okays[o];
      }
      this.allOkay = ok;
    },
    async pollSignatureProcess() {
      await this.$store.dispatch("application/fetchSignatureProcess");
    },
    async downloadFile(id, fileName) {
      if (!id) return;
      if (id === "-1") return;
      await this.$store.dispatch("drawer/download", id);
      let blob = this.$store.getters["drawer/getFile"](id);
      FileSaver.saveAs(blob, fileName);
    },
    async gotoSign() {
      let flowConfig = this.$store.getters["application/flowConfig"];

      await this.savePosition({
        flowPosition: nextStepConfigOverride(
          flowConfig,
          FLOW_POSITION.CONTRACT_CHECK
        ),
      });
    },
  },
};
</script>

<style scoped lang="scss">
.requirement {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 1rem 0;

  &__icon {
    height: 40px;
    width: 40px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 182, 127, 0.45);
  }
}

.signature-process-info-card {
  background-color: #f3f5f9;
  border-radius: 8px;
  padding: 1rem;
}
</style>
